import React from 'react';
import { useT } from '../../i18n/useT';
import { Helmet } from 'react-helmet-async';
import LegalPage, { LegalSection, LegalPlaceholder, LegalNote } from '../../components/legal/LegalPage';
import '../../components/legal/LegalPage.css';

export default function CookiePolicyPage() {
  const t = useT();
  const lp = t.legalPages;
  return (
    <>
      <Helmet>
        <title>{lp.cookies.title} – Docudi</title>
        <meta name="description" content={lp.cookies.metaDesc} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <LegalPage title={lp.cookies.title} lastUpdated="[PLACEHOLDER: Date]">

        <LegalSection id="what-are-cookies" title="1. What Are Cookies?">
          <div className="legal-section__content">
            <p>
              Cookies are small text files that are stored on your device (computer, tablet, or smartphone)
              when you visit a website or use a web application. They help services remember your preferences
              and understand how you interact with the product.
            </p>
            <p>
              We also use similar technologies such as local storage, session storage, and pixels where relevant.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="how-we-use" title="2. How We Use Cookies">
          <div className="legal-section__content">
            <p>Docudi uses cookies for the following purposes:</p>

            <h3>Essential Cookies (always active)</h3>
            <p>
              These cookies are necessary for the app to function and cannot be switched off. They are usually
              set in response to actions you take (logging in, setting preferences, form submissions).
            </p>

            <h3>Functional Cookies (with consent)</h3>
            <p>
              These cookies remember choices you make (e.g. language, UI preferences) to provide a more
              personalised experience. Disabling them may affect some features.
            </p>

            <h3>Analytics Cookies (with consent)</h3>
            <p>
              These help us understand how visitors interact with the app, which features are used most, and
              where improvements are needed. Data is aggregated and <LegalPlaceholder label="anonymised / pseudonymised" />.
            </p>

            <h3>Marketing Cookies (with consent)</h3>
            <p>
              <LegalPlaceholder label="Describe any marketing/advertising cookies — or remove this section if you don't use them." />
            </p>
          </div>
        </LegalSection>

        <LegalSection id="cookies-table" title="3. Cookies We Use">
          <div className="legal-section__content">
            <LegalNote>
              Replace the table below with your actual cookies. You can audit your cookies using browser
              developer tools (Application → Cookies) or a service like CookieBot or OneTrust.
            </LegalNote>
            <div style={{ overflowX: 'auto', marginTop: 'var(--space-4)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                <thead>
                  <tr style={{ background: 'var(--color-gray-50)', borderBottom: '2px solid var(--color-gray-100)' }}>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Purpose</th>
                    <th style={thStyle}>Duration</th>
                    <th style={thStyle}>Provider</th>
                  </tr>
                </thead>
                <tbody>
                  <CookieRow
                    name="session"
                    type="Essential"
                    purpose="Maintains user login session"
                    duration="Session"
                    provider="Docudi"
                  />
                  <CookieRow
                    name="csrf_token"
                    type="Essential"
                    purpose="Protects against cross-site request forgery"
                    duration="Session"
                    provider="Docudi"
                  />
                  <CookieRow
                    name="cookieConsent"
                    type="Essential"
                    purpose="Stores your cookie consent preferences"
                    duration="1 year"
                    provider="Docudi"
                  />
                  <CookieRow
                    name={<LegalPlaceholder label="[analytics cookie name]" />}
                    type="Analytics"
                    purpose={<LegalPlaceholder label="[describe purpose]" />}
                    duration={<LegalPlaceholder label="[duration]" />}
                    provider={<LegalPlaceholder label="[provider name]" />}
                  />
                  <CookieRow
                    name={<LegalPlaceholder label="[functional cookie name]" />}
                    type="Functional"
                    purpose={<LegalPlaceholder label="[describe purpose]" />}
                    duration={<LegalPlaceholder label="[duration]" />}
                    provider={<LegalPlaceholder label="Docudi" />}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </LegalSection>

        <LegalSection id="managing" title="4. Managing Your Cookie Preferences">
          <div className="legal-section__content">
            <p>
              You can manage your cookie preferences at any time using our cookie banner. Click "Manage
              Preferences" in the banner at the bottom of the page, or contact us to reset your preferences.
            </p>
            <p>
              You can also control cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul>
              <li>View what cookies are stored and delete them individually</li>
              <li>Block third-party cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block all cookies (note: this may break app functionality)</li>
              <li>Delete all cookies when you close the browser</li>
            </ul>
            <p>
              For instructions, visit the help pages of your browser:
              <br />
              <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a>{' · '}
              <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Firefox</a>{' · '}
              <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a>{' · '}
              <a href="https://support.microsoft.com/en-us/topic/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer">Edge</a>
            </p>
          </div>
        </LegalSection>

        <LegalSection id="legal-basis" title="5. Legal Basis">
          <div className="legal-section__content">
            <p>
              Essential cookies are set on the basis of our legitimate interest (Art. 6(1)(f) GDPR) and are
              necessary for the provision of the service.
            </p>
            <p>
              All non-essential cookies (functional, analytics, marketing) are set only with your explicit
              consent (Art. 6(1)(a) GDPR), which you provide via our cookie banner. You may withdraw your
              consent at any time.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="changes" title="6. Changes to This Policy">
          <div className="legal-section__content">
            <p>
              We may update this Cookie Policy to reflect changes in the cookies we use or for other
              operational, legal, or regulatory reasons. Please re-visit this page regularly to stay
              informed about our use of cookies.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="contact" title="7. Contact">
          <div className="legal-section__content">
            <p>
              Questions about this policy? Contact us at:{' '}
              <LegalPlaceholder label="privacy@example.com" />
            </p>
          </div>
        </LegalSection>

      </LegalPage>
    </>
  );
}

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--color-gray-700)',
};

const tdStyle = {
  padding: '10px 12px',
  color: 'var(--color-gray-600)',
  borderBottom: '1px solid var(--color-gray-100)',
  verticalAlign: 'top',
};

function CookieRow({ name, type, purpose, duration, provider }) {
  const typeColors = {
    Essential:  { bg: '#ECFDF5', color: '#065F46' },
    Analytics:  { bg: '#EFF6FF', color: '#1E40AF' },
    Functional: { bg: '#FAF5FF', color: '#6B21A8' },
    Marketing:  { bg: '#FFF7ED', color: '#9A3412' },
  };
  const style = typeColors[type] || { bg: '#F9FAFB', color: '#374151' };

  return (
    <tr>
      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{name}</td>
      <td style={tdStyle}>
        <span style={{ background: style.bg, color: style.color, padding: '2px 8px', borderRadius: '99px', fontSize: '0.75rem', fontWeight: 600 }}>
          {type}
        </span>
      </td>
      <td style={tdStyle}>{purpose}</td>
      <td style={tdStyle}>{duration}</td>
      <td style={tdStyle}>{provider}</td>
    </tr>
  );
}
